import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { AnimationShell, Container } from '@/components';
import { useBreadcrumbs } from '@/features/header';
import { useNotifications } from '@/features/notifications';
import { BasicInfoForm, BasicInfoFormFields, useGetCurrentUserQuery, useUpdateUserMutation } from '@/features/users';
import { useRegisterGAPageView } from '@/hooks';
import { EmailNotificationsForm } from '../email-notifications';
import { AccountClosureDisclaimer } from '../account-closure-disclaimer';

export function ProfileSettingsPage() {
  const { t } = useTranslation(['settings']);

  useBreadcrumbs(() => [{ title: t(`Profile settings`) }]);
  useRegisterGAPageView('Profile settings');
  const { add } = useNotifications();

  const { data: user } = useGetCurrentUserQuery();

  const [userMutation] = useUpdateUserMutation();

  const onSubmit = useCallback(
    (data: BasicInfoFormFields) => {
      userMutation(data)
        .unwrap()
        .then(() => {
          add({
            id: new Date().toString(),
            title: t('Profile updated'),
            description: t('Your profile has been updated'),
          });
        })
        .catch(() => {
          add({
            id: new Date().toString(),
            title: t('ERROR'),
            description: t('There was an error updating your profile. Please try again later.'),
          });
        });
    },
    [userMutation, add, t]
  );

  return (
    <Container>
      <div className="space-y-4">
        <AnimationShell element="article" type="opacity" duration={0} className="mt-4 h-auto overflow-hidden">
          <div className="p-2">{user ? <BasicInfoForm user={user} onSubmit={onSubmit} /> : null}</div>
        </AnimationShell>

        <div className="border-t border-gray-200" />
        <AnimationShell element="article" type="opacity" duration={0} className="h-auto overflow-hidden">
          <div className="p-2">{user ? <EmailNotificationsForm userId={user.id} /> : null}</div>
        </AnimationShell>

        <div className="border-t border-gray-200" />

        <div className="p-2 pb-12">
          <AccountClosureDisclaimer />
        </div>
      </div>
    </Container>
  );
}
